//
//  AmwellExperience.Web
//  MyHealth+
//
//  Created by jinyeob kim on 1/23/20.
//  Copyright © 2020 Facebook. All rights reserved.
//

import awsdk from 'awsdk';
import flatten from 'lodash/flatten';
import {
  Awsdk,
  ChatReport,
  Consumer,
  EstimatedVisitCost,
  PracticeList,
  Provider,
  VisitContext,
  FirstAvailableConfiguration,
  ProviderDetails,
  Visit,
  Condition,
  Allergy,
  Vitals,
  Medication,
  HealthPlan,
  Country,
  RelationshipToSubscriberCode,
  Subscription,
  PaymentMethod,
  Pharmacy,
  VisitSummary,
  VideoConsole,
  Practice,
  Appointment,
  HealthDocumentRecord,
  SystemConfiguration,
  Wizard
} from '../sdk/types';
import { amwellBus } from '../index';
import * as types from './types';
import { configuration } from './constants';

import AwsdkError from './errors/AwsdkError';
import AmwellFactory from './AmwellFactory';

import AuthenticationService from './services/AuthenticationService';
import TestMyComputerService from './services/TestMyComputerService';
import ConsumerService from './services/ConsumerService';
import InitializationService from './services/InitializationService';
import PracticeService from './services/PracticeService';
import ProviderService from './services/ProviderService';
import VisitService from './services/VisitService';
import PharmacyService from './services/PharmacyService';
import AppointmentService from './services/AppointmentService';
import { useWebRTC } from './utils';

export class AmwellClient extends AmwellFactory {
  sdk: Awsdk;

  initializationService: InitializationService;
  testMyComputerService: TestMyComputerService;
  authenticationService: AuthenticationService;
  consumerService: ConsumerService;
  practiceService: PracticeService;
  providerService: ProviderService;
  visitService: VisitService;
  pharmacyService: PharmacyService;
  appointmentService: AppointmentService;

  constructor() {
    super();
    const sdk: Awsdk = new awsdk.AWSDK();

    try {
      const { instanceState } = this.get();

      if (instanceState) {
        sdk.restoreInstanceState(instanceState);
      }
    } catch (e) {
      // Removes stale/invalid storage item
      localStorage.removeItem('awsdk');
    }

    this.sdk = sdk;

    this.initializationService = new InitializationService(this);
    this.testMyComputerService = new TestMyComputerService(this);
    this.authenticationService = new AuthenticationService(this);
    this.consumerService = new ConsumerService(this);
    this.practiceService = new PracticeService(this);
    this.providerService = new ProviderService(this);
    this.visitService = new VisitService(this);
    this.pharmacyService = new PharmacyService(this);
    this.appointmentService = new AppointmentService(this);
  }

  /**
   * Method (promise): execute
   * @desc The amwellAxiosStub calls this method to call the AmwellClient services' methods.
   * @param {string} fnName should match any methods within the Services class constructor,
   * or any neighboring methods in AmwellClient Class
   * @param {*} data should always be an object that contains the params to the matching method(fnName)
   */
  async execute<T extends keyof types.Dispatch>(
    fnName: T,
    data: types.ExecuteData<T>
  ): Promise<types.ExecuteReturn<T>> {
    if (!this.sdk.initialized) {
      await this.init({ configuration });
    }

    if (typeof this[fnName] !== 'function') {
      throw new AwsdkError(this.execute.name, `No service matching ${fnName}`);
    }

    if ((fnName as keyof this) === 'sdk') {
      throw new AwsdkError(this.execute.name, 'Should not directly call sdk');
    }

    const res = await this[fnName](data);
    this.set({ instanceState: this.sdk.instanceState });
    return res as Promise<types.ExecuteReturn<T>>;
  }

  // //////////////////////////////////////////////// //
  // //////////////////////////////////////////////// //
  // //////////////////////////////////////////////// //

  // ////////////// //
  // Initialization //
  // ////////////// //

  /**
   * Method (promise): init
   * @desc Initializes the awsdk so that AmwellClient Services to be used.
   * @param {Object} data
   * @param {Object} data.configuration
   */
  init(data: types.InitData): Promise<boolean> {
    return this.initializationService.initialize(data.configuration);
  }

  // //////////////////////////////////////////////// //
  // //////////////////////////////////////////////// //
  // //////////////////////////////////////////////// //

  // //////////////// //
  // Test My Computer //
  // //////////////// //

  /**
   * Method (promise): getTestWizard
   * @desc Gets the test wizard to test hardwares required for a WebRTC visit
   * @param {Object} data
   * @param {Object} data.configuration
   */
  getTestWizard(data: types.GetTestWizardData): Wizard {
    return this.testMyComputerService.getTestMyComputerWizard(data.configuration);
  }

  // //////////////////////////////////////////////// //
  // //////////////////////////////////////////////// //
  // //////////////////////////////////////////////// //

  // ///////////// //
  // Amwell System //
  // ///////////// //

  /**
   * Method (promise): getHealthPlans
   * @desc Gets health plans on the amwell system.
   */
  getHealthPlans(): Promise<HealthPlan[]> {
    return this.sdk.getHealthPlans().catch(err => {
      throw new AwsdkError(this.getHealthPlans.name, err);
    });
  }

  /**
   * Method (promise): getCountries
   * @desc Gets countries on the amwell system.
   */
  getCountries(): Promise<Country[]> {
    return this.sdk.getCountries().catch(err => {
      throw new AwsdkError(this.getCountries.name, err);
    });
  }

  /**
   * Method (promise): getRelationshipsToSubscriber
   * @desc Gets relationships to the subscriber on the amwell system.
   */
  getRelationshipsToSubscriber(): Promise<RelationshipToSubscriberCode[]> {
    return this.sdk.getRelationshipsToSubscriber().catch(err => {
      throw new AwsdkError(this.getRelationshipsToSubscriber.name, err);
    });
  }

  /**
   * Method (promise): getSystemConfiguration
   * @desc Gets the configuration on the amwell system.
   */
  getSystemConfiguration(): SystemConfiguration {
    return this.sdk.getSystemConfiguration();
  }

  // ////////////// //
  // Authentication //
  // ////////////// //

  /**
   * Method (promise): loginWithToken
   * @desc Login with token and return the authenticated consumer.
   * @param {Object} data
   * @param {string} data.token
   */
  async loginWithToken(data: types.LoginWithTokenData): Promise<Consumer> {
    const authentication = await this.authenticationService.authenticateWithToken(data.visitToken);
    this.set({ authentication });
    const consumer = await this.consumerService.getConsumer(authentication);
    this.set({ consumer, activeConsumer: consumer });
    return consumer;
  }

  /**
   * Method (promise): loginWithToken
   * @desc Logout of authenticated consumer.
   */
  async logout(): Promise<boolean> {
    const instance = this.get();

    if (this.sdk.initialized && instance.consumer) {
      this.authenticationService.clearAuthentication(instance.consumer);
    }
    await this.clear();
    return true;
  }

  /**
   * Method (promise): requestPasswordReset
   * @desc Sends the user an email to data.email if an account is found.
   * @param {Object} data
   * @param {string} data.email
   * @param {string} data.lastName
   * @param {Object} data.dob
   */
  requestPasswordReset(data: types.RequestPasswordResetData): Promise<boolean> {
    return this.authenticationService.requestPasswordReset(data.email, data.lastName, data.dob);
  }

  // //////////////////////////////////////////////// //
  // //////////////////////////////////////////////// //
  // //////////////////////////////////////////////// //

  // //////// //
  // Consumer //
  // //////// //

  /**
   * Method (promise): updateConsumerLegalResidence
   * @desc Updates consumer's legal residence and returns the consumer
   * @param {Object} data
   * @param {Object} data.updateConsumerLegalResidenceForm
   */
  async updateConsumerLegalResidence(
    data: types.UpdateConsumerLegalResidenceData
  ): Promise<Consumer> {
    const instance = this.get();
    const consumer = instance.consumer as Consumer;

    const consumerUpdate = this.consumerService.newConsumerUpdate();

    consumerUpdate.legalResidence = data.stateCode;
    consumerUpdate.legalResidenceCountryCode = data.countryCode || 'US';

    const updatedConsumer = await this.consumerService.updateConsumer(consumer, consumerUpdate);
    const dependents = await this.consumerService.getDependents(updatedConsumer);

    let activeConsumer = updatedConsumer;

    if (instance.activeConsumer?.isDependent) {
      const dependent = dependents.find(d => {
        return d.id.persistentId === instance.activeConsumer?.id.persistentId;
      });
      if (dependent) {
        activeConsumer = dependent;
      }
    }

    this.set({ consumer: updatedConsumer, activeConsumer });
    return updatedConsumer;
  }

  /**
   * Method (promise): setConsumer
   * @desc Sets the active consumer and removes all the stored data related to the previous active consumer.
   * @param {Object} data
   * @param {Object} data.consumer
   */
  async setConsumer(data: types.SetConsumerData): Promise<Consumer> {
    await this.clear({ opts: 'session' });
    this.set({ activeConsumer: data.consumer });
    return data.consumer;
  }

  // //////////////////////////////////////////////// //
  // //////////////////////////////////////////////// //
  // //////////////////////////////////////////////// //

  // //////////////////////////// //
  // Consumer (Parent) Dependents //
  // //////////////////////////// //

  /**
   * Method (promise): getDependents
   * @desc Get list of dependents related to the parent consumer.
   */
  getDependents(): Promise<Consumer[]> {
    const instance = this.get();
    const consumer = instance.consumer as Consumer;

    return this.consumerService.getDependents(consumer);
  }

  /**
   * Method (promise): addDependent
   * @desc Adds a dependent to the parent consumer and returns the new list of dependents.
   * @param {Object} data
   * @param {Object} data.registerDependentForm
   */
  async addDependent(data: types.AddDependentData): Promise<Consumer[]> {
    const instance = this.get();
    const consumer = instance.consumer as Consumer;
    const form = data.registerDependentForm;

    const dependentRegistration = this.consumerService.newDependentRegistration();

    dependentRegistration.firstName = form.firstName;
    dependentRegistration.middleName = form.middleName;
    dependentRegistration.lastName = form.lastName;
    dependentRegistration.gender = form.gender;
    dependentRegistration.dob = form.dob;
    dependentRegistration.performDependentCheck = true;

    await this.consumerService.registerDependent(consumer, dependentRegistration);

    const updatedConsumer = await this.consumerService.getUpdatedConsumer(consumer);

    this.set({ consumer: updatedConsumer });
    return this.consumerService.getDependents(updatedConsumer);
  }

  // //////////////////////////////////////////////// //
  // //////////////////////////////////////////////// //
  // //////////////////////////////////////////////// //

  // ////////////////////////// //
  // Active Consumer Pharmacies //
  // ////////////////////////// //

  /**
   * Method (promise): searchPharmacies
   * @desc Find pharmacies that match search critera
   * @param {Object} data
   * @param {string} data.city
   * @param {Object} data.state
   * @param {string} data.zipCode
   * @param {string} data.pharmacyType
   */
  searchPharmacies(data: types.SearchPharmaciesData): Promise<Pharmacy[]> {
    const { city, state, zipCode, pharmacyType } = data;
    return this.pharmacyService.searchPharmacies(city, state, zipCode, pharmacyType);
  }

  /**
   * Method (promise): getAllPharmacies
   * @desc Find pharmacies that match search critera
   * @param {Object} data
   * @param {number} data.latitude
   * @param {number} data.longitude
   * @param {string} data.pharmacyType
   * @param {number} data.radius
   */
  getAllPharmacies(data: types.GetAllPharmaciesPayloadData): Promise<Pharmacy[]> {
    const { latitude, longitude, radius = 50, pharmacyType } = data;
    return this.pharmacyService.getAllPharmacies(latitude, longitude, radius, pharmacyType);
  }

  /**
   * Method (promise): updatePreferredPharamacy
   * @desc Sets the users preferred pharmacy
   * @param {Object} data
   * @param {Object} data.pharmacy
   */
  async updatePreferredPharmacy(data: types.UpdatePreferredPharmacyData): Promise<Pharmacy> {
    const instance = this.get();
    let consumer = instance.activeConsumer as Consumer;

    consumer = await this.pharmacyService.updatePreferredPharmacy(consumer, data.pharmacy);
    const updatedConsumer = await this.consumerService.getUpdatedConsumer(consumer);

    this.set({ consumer: updatedConsumer });
    return data.pharmacy;
  }

  /**
   * Method (promise): getPreferredPharamacy
   * @desc Get the active consumer's preferred pharmacy
   */
  getPreferredPharmacy(): Promise<Pharmacy | null> {
    const instance = this.get();
    const consumer = instance.activeConsumer as Consumer;

    return this.pharmacyService.getPreferredPharmacy(consumer);
  }

  // //////////////////////////////////////////////// //
  // //////////////////////////////////////////////// //
  // //////////////////////////////////////////////// //

  // ////////////////////////// //
  // Active Consumer Appointment //
  // ////////////////////////// //

  /**
   * Method (promise): getAppointments
   * @desc Gets the consumer and consumer's dependents' list of appointments
   */
  async getAppointments(): Promise<Appointment[]> {
    const instance = this.get();
    const consumer = instance.consumer as Consumer;
    const dependents = await this.consumerService.getDependents(consumer);

    const appointments = await Promise.all(
      [consumer, ...dependents].map(c => this.appointmentService.getAppointments(c))
    );

    return flatten(appointments);
  }

  /**
   * Method (promise): getAppointments
   * @desc Gets an updated appointment off of the appointments list
   * @param {Object} data
   * @param {Object} data.appointment
   */
  async getAppointment(data: types.GetAppointmentData): Promise<Appointment> {
    const appointment = await this.appointmentService.getAppointment(data.appointment);
    this.set({ appointment });
    return appointment;
  }

  // //////////////////////////////////////////////// //
  // //////////////////////////////////////////////// //
  // //////////////////////////////////////////////// //

  // /////////////////////////////// //
  // Active Consumer Medical History //
  // /////////////////////////////// //

  /**
   * Method (promise): getMedications
   * @desc Get list of medications related to the active consumer.
   */
  async getMedications(): Promise<Medication[]> {
    const instance = this.get();
    const consumer = instance.activeConsumer as Consumer;

    const list = await this.consumerService.getMedications(consumer);
    return list.medications;
  }

  /**
   * Method (promise): updateMedications
   * @desc Updates medications related to the active consumer and returns it.
   * @param {Object} data
   * @param {Object[]} data.medications
   */
  updateMedications(data: types.UpdateMedicationsData): Promise<Medication[]> {
    const instance = this.get();
    const consumer = instance.activeConsumer as Consumer;

    return this.consumerService.updateMedications(consumer, data.medications);
  }

  /**
   * Method (promise): searchMedications
   * @desc search medications.
   * @param {Object} data
   * @param {Object[]} data.searchText
   */
  async searchMedications(data: types.SearchMedicationsData): Promise<Medication[]> {
    const instance = this.get();
    const consumer = instance.activeConsumer as Consumer;
    const list = await this.consumerService.searchMedications(consumer, data.searchText);

    return list.medications;
  }

  /**
   * Method (promise): getConditions
   * @desc Get list of conditions related to the active consumer.
   */
  getConditions(): Promise<Condition[]> {
    const instance = this.get();
    const consumer = instance.activeConsumer as Consumer;

    return this.consumerService.getConditions(consumer);
  }

  /**
   * Method (promise): updateConditions
   * @desc Updates conditions related to the active consumer and returns it.
   * @param {Object} data
   * @param {Object[]} data.conditions
   */
  updateConditions(data: types.UpdateConditionsData): Promise<Condition[]> {
    const instance = this.get();
    const consumer = instance.activeConsumer as Consumer;

    return this.consumerService.updateConditions(consumer, data.conditions);
  }

  /**
   * Method (promise): getAllergies
   * @desc Get list of allergies related to the active consumer.
   */
  getAllergies(): Promise<Allergy[]> {
    const instance = this.get();
    const consumer = instance.activeConsumer as Consumer;

    return this.consumerService.getAllergies(consumer);
  }

  /**
   * Method (promise): updateAllergies
   * @desc Updates allergies related to the active consumer and returns it.
   * @param {Object} data
   * @param {Object[]} data.allergies
   */
  updateAllergies(data: types.UpdateAllergiesData): Promise<Allergy[]> {
    const instance = this.get();
    const consumer = instance.activeConsumer as Consumer;

    return this.consumerService.updateAllergies(consumer, data.allergies);
  }

  /**
   * Method (promise): getVitals
   * @desc Get vitals related to the active consumer.
   */
  getVitals(): Promise<Vitals> {
    const instance = this.get();
    const consumer = instance.activeConsumer as Consumer;

    return this.consumerService.getVitals(consumer);
  }

  /**
   * Method (promise): updateVitals
   * @desc Updates vitals related to the active consumer and returns it.
   * @param {Object} data
   * @param {Object} data.vitals
   */
  updateVitals(data: types.UpdateVitalsData): Promise<Vitals> {
    const instance = this.get();
    const consumer = instance.activeConsumer as Consumer;

    return this.consumerService.updateVitals(consumer, data.vitals);
  }

  /**
   * Method (promise): getHealthDocuments
   * @desc Gets all the health documents related to the active consumer
   */
  getHealthDocuments(): Promise<HealthDocumentRecord[]> {
    const instance = this.get();
    const consumer = instance.activeConsumer as Consumer;

    return this.consumerService.searchHealthDocumentRecords(consumer);
  }

  /**
   * Method (promise): searchHealthDocuments
   * @desc Search all the health documents related to the active consumer
   */
  searchHealthDocuments(): Promise<HealthDocumentRecord[]> {
    const instance = this.get();
    const consumer = instance.activeConsumer as Consumer;

    return this.consumerService.searchHealthDocumentRecords(consumer);
  }

  /**
   * Method (promise): addHealthDocument
   * @desc Uploads health document related to the active consumer and returns all the health documents.
   * @param {Object} data
   * @param {Object} data.file
   * @param {string} data.fileName
   * @param {Object} data.visit
   * @param {string} data.comment
   */
  async addHealthDocument(data: types.AddHealthDocumentData): Promise<HealthDocumentRecord[]> {
    const instance = this.get();
    const consumer = instance.activeConsumer as Consumer;

    await this.consumerService.addHealthDocumentAttachment(
      consumer,
      data.file,
      data.fileName,
      data.visit,
      data.comment
    );
    return this.consumerService.searchHealthDocumentRecords(consumer);
  }

  /**
   * Method (promise): removeHealthDocument
   * @desc Removes the Document Record from the related active consumer and returns all the health documents.
   * @param {Object} data
   * @param {Object} data.healthDocument
   */
  async removeHealthDocument(
    data: types.RemoveHealthDocumentData
  ): Promise<HealthDocumentRecord[]> {
    const instance = this.get();
    const consumer = instance.activeConsumer as Consumer;

    await this.consumerService.removeHealthDocumentRecord(consumer, data.healthDocument);
    return this.consumerService.searchHealthDocumentRecords(consumer);
  }

  // //////////////////////////////////////////////// //
  // //////////////////////////////////////////////// //
  // //////////////////////////////////////////////// //

  // ///////////////////////// //
  // Active Consumer Insurance //
  // ///////////////////////// //

  /**
   * Method (promise): getInsuranceSubscription
   * @desc Gets insurance subscription related to the active consumer.
   */
  getInsuranceSubscription(): Promise<Subscription> {
    const instance = this.get();
    const consumer = instance.activeConsumer as Consumer;

    return this.consumerService.getInsuranceSubscription(consumer);
  }

  /**
   * Method (promise): updateInsuranceSubscription
   * @desc Updates insurance subscriptiopn related to the active consumer and returns the updated consumer.
   * @param {Object} data
   * @param {Object} data.updateInsuranceSubscriptionForm
   */
  async updateInsuranceSubscription(
    data: types.UpdateInsuranceSubscriptionData
  ): Promise<Consumer> {
    const instance = this.get();
    const consumer = instance.activeConsumer as Consumer;
    const form = data.updateInsuranceSubscriptionForm;

    const subscriptionUpdate = this.consumerService.newSubscriptionUpdate();

    subscriptionUpdate.ignoreEligibilityChecks = form.ignoreEligibilityChecks || false;
    subscriptionUpdate.healthPlan = form.healthPlan;
    subscriptionUpdate.relationshipToSubscriberCode = form.relationshipToSubscriberCode;
    subscriptionUpdate.subscriberId = form.subscriberId;
    subscriptionUpdate.subscriberSuffix = form.subscriberSuffix;

    if (form.relationshipToSubscriberCode.displayName !== 'SUBSCRIBER') {
      subscriptionUpdate.primarySubscriberDateOfBirth = form.primarySubscriberDateOfBirth as Date;
      subscriptionUpdate.primarySubscriberFirstName = form.primarySubscriberFirstName as string;
      subscriptionUpdate.primarySubscriberLastName = form.primarySubscriberLastName as string;
    }

    await this.consumerService.updateInsuranceSubscription(consumer, subscriptionUpdate);
    const updatedConsumer = await this.consumerService.getUpdatedConsumer(consumer);
    this.set({ activeConsumer: updatedConsumer });
    return updatedConsumer;
  }

  /**
   * Method (promise): removeInsuranceSubscription
   * @desc Removes insurance subscription related to the active consumer and returns the updated consumer.
   */
  async removeInsuranceSubscription(): Promise<Consumer> {
    const instance = this.get();
    const consumer = instance.activeConsumer as Consumer;

    await this.consumerService.removeInsuranceSubscription(consumer);
    const updatedConsumer = await this.consumerService.getUpdatedConsumer(consumer);
    this.set({ activeConsumer: updatedConsumer });
    return updatedConsumer;
  }

  // //////////////////////////////////////////////// //
  // //////////////////////////////////////////////// //
  // //////////////////////////////////////////////// //

  // /////////////////////// //
  // Active Consumer Payment //
  // /////////////////////// //

  /**
   * Method (promise): getPaymentMethod
   * @desc Gets payment method related to the active consumer and returns it.
   */
  getPaymentMethod(): Promise<PaymentMethod | null> {
    const instance = this.get();
    const consumer = instance.activeConsumer as Consumer;

    return this.consumerService.getPaymentMethod(consumer);
  }

  /**
   * Method (promise): updatePaymentMethod
   * @desc Updates payment method related to the active consumer and returns the updated payment method.
   * @param {Object} data
   * @param {Object} data.updatePaymentMethodForm
   */
  updatePaymentMethod(data: types.UpdatePaymentMethodData): Promise<PaymentMethod> {
    const instance = this.get();
    const consumer = instance.activeConsumer as Consumer;
    const form = data.updatePaymentMethodForm;
    const paymentRequest = this.consumerService.newPaymentRequest();

    paymentRequest.nameOnCard = form.cardholderName;
    paymentRequest.creditCardNumber = form.cardNumber;
    paymentRequest.creditCardSecCode = form.cvv;
    paymentRequest.creditCardMonth = form.expiryMonth;
    paymentRequest.creditCardYear = form.expiryYear;
    paymentRequest.address1 = form.address1;
    paymentRequest.address2 = form.address2;
    paymentRequest.city = form.city;
    // state = state code (ex: 'UT')
    paymentRequest.state = form.state;
    // country = country code (ex: 'US')
    paymentRequest.country = form.country || 'US';
    paymentRequest.creditCardZip = form.zipCode;

    return this.consumerService.updatePaymentMethod(consumer, paymentRequest);
  }

  // //////////////////////////////////////////////// //
  // //////////////////////////////////////////////// //
  // //////////////////////////////////////////////// //

  // ///////////////////////////// //
  // Active Consumer Finding Visit //
  // ///////////////////////////// //

  /**
   * Method (promise): practices
   * @desc Get object of practices and practiceCategories related to the active consumer.
   */
  practices(): Promise<PracticeList> {
    const instance = this.get();
    const consumer = instance.activeConsumer as Consumer;
    return this.practiceService.getPractices(consumer);
  }

  /**
   * Method (promise): updatePractice
   * @desc Updates the stored practice
   * @param {Object} data
   * @param {Object} data.practice
   */
  updatePractice(data: types.UpdatePracticeData): Practice {
    this.set({ practice: data.practice });
    return data.practice;
  }

  /**
   * Method (promise): addServiceKey
   * @desc Add Service key
   * @param {Ojbect} data
   * @param {string} data.key
   */
  async addServiceKey(data: types.AddServiceKeyData): Promise<Consumer> {
    const instance = this.get();
    const consumer = instance.activeConsumer as Consumer;
    const updateConsumer = await this.consumerService.addServiceKey(consumer, data.key);
    this.set({ consumer: updateConsumer, activeConsumer: updateConsumer });
    return updateConsumer;
  }

  /**
   * Method (promise): estimatedVisitCost
   * @desc Allows for the retrieval of the estimated cost of a visit, before insurance, with a given Provider.
   * @param {Object} data
   * @param {Object} data.provider
   */
  estimatedVisitCost(data: types.EstimatedVisitCostData): Promise<EstimatedVisitCost> {
    const instance = this.get();
    const consumer = instance.activeConsumer as Consumer;

    return this.providerService.getEstimatedVisitCost(consumer, data.providerDetails);
  }

  /**
   * Method (promise): hasFirstAvailable
   * @desc Boolean to see if there is first available
   * @param {Object} data
   * @param {Object} data.practice
   */
  async hasFirstAvailable(data: types.HasFirstAvailableData): Promise<boolean> {
    const instance = this.get();
    const consumer = instance.activeConsumer as Consumer;

    const specialty = await this.practiceService.getFirstAvailableConfiguration(
      consumer,
      data.practice
    );
    return Boolean(specialty);
  }

  /**
   * Method (promise): getFirstAvailable
   * @desc Gets first specialty related to the active consumer and selected practice
   * @param {Object} data
   * @param {Object} data.practice
   */
  async getFirstAvailable(data: types.GetFirstAvailableData): Promise<FirstAvailableConfiguration> {
    const instance = this.get();
    const consumer = instance.activeConsumer as Consumer;

    const specialty = await this.practiceService.getFirstAvailableConfiguration(
      consumer,
      data.practice
    );
    const hasContext =
      specialty?.id.encryptedId === instance.visitContext?.firstAvailableConfigurationId;

    const keys = hasContext
      ? [types.StorageKey.PROVIDER]
      : [types.StorageKey.VISIT_CONTEXT, types.StorageKey.PROVIDER];
    await this.remove({ keys });
    this.set({ specialty });
    return specialty;
  }

  /**
   * Method (promise): providers
   * @desc Get a list of providers related to the active consumer and selected practice.
   * @param {Object} data
   * @param {Object} data.practice
   */
  providers(data: types.ProvidersData): Promise<Provider[]> {
    const instance = this.get();
    const consumer = instance.activeConsumer as Consumer;

    return this.providerService.findProvidersInPractice(consumer, data.practice);
  }

  /**
   * Method (promise): providerDetail
   * @desc Get provider's details related to the active consumer and selected provider.
   * @param {Object} data
   * @param {Object} data.provider
   */
  async providerDetail(data: types.ProviderDetailData): Promise<ProviderDetails> {
    const instance = this.get();
    const consumer = instance.activeConsumer as Consumer;

    const providerDetails = await this.providerService.getProviderDetails(consumer, data.provider);
    const hasContext = providerDetails.id.encryptedId === instance.visitContext?.providerId;

    const keys = hasContext
      ? [types.StorageKey.SPECIALTY]
      : [types.StorageKey.VISIT_CONTEXT, types.StorageKey.SPECIALTY];

    await this.remove({ keys });
    this.set({ provider: providerDetails });
    return providerDetails;
  }

  // //////////////////////////////////////////////// //
  // //////////////////////////////////////////////// //
  // //////////////////////////////////////////////// //

  // ///////////// //
  // Visit Context //
  // ///////////// //

  /**
   * Method (promise): getVisitContext
   * @desc Gets visit context related to the active consumer and selected provider or specialty.
   */
  async getVisitContext(): Promise<types.VisitContextAndDisclaimersAcknowledged> {
    const instance = this.get();
    const consumer = instance.activeConsumer as Consumer;
    const provider = instance.provider as ProviderDetails;
    const firstAvailableConfiguration = instance.specialty as FirstAvailableConfiguration;
    const disclaimersAcknowledged = instance.disclaimersAcknowledged as boolean;
    const speedPassVisit = instance.speedPassVisit as Visit;
    const visitContext = instance.visitContext as VisitContext;

    const visitContextAndDisclaimersAcknowledged = {
      visitContext,
      disclaimersAcknowledged
    };

    if (visitContext) {
      return visitContextAndDisclaimersAcknowledged;
    }

    const providerOrFirstAvailableConfiguration = provider || firstAvailableConfiguration;

    const newVisitContext = await this.visitService.getVisitContext(
      consumer,
      providerOrFirstAvailableConfiguration
    );

    visitContextAndDisclaimersAcknowledged.visitContext = newVisitContext;
    visitContextAndDisclaimersAcknowledged.visitContext.callbackNumber = consumer.phone || '';
    visitContextAndDisclaimersAcknowledged.disclaimersAcknowledged = !!speedPassVisit;

    this.set(visitContextAndDisclaimersAcknowledged);
    return visitContextAndDisclaimersAcknowledged;
  }

  /**
   * Method (promise): createVisitContextWithAppointment
   * @desc Gets visit context related to the appointment.
   * @param {Object} data
   * @param {Object} data.appointment
   */
  async createVisitContextWithAppointment(
    data: types.CreateVisitContextWithAppointmentData
  ): Promise<types.AppointmentVisitContextRequirements> {
    const visitContext = await this.visitService.getVisitContextForAppointment(data.appointment);

    visitContext.callbackNumber = data.appointment.consumer.phone || '';
    visitContext.modalityType = data.appointment.modality.modalityType;

    const appointment = {
      activeConsumer: data.appointment.consumer,
      provider: data.appointment.assignedProvider,
      disclaimersAcknowledged: false,
      visitContext
    };

    this.set(appointment);
    return appointment;
  }

  /**
   * Method (promise): updateVisitContext
   * @desc Gets the active visit context and updates it.
   * @param {Object} data
   * @param {Object} data.updateVisitContextForm
   */
  async updateVisitContext(
    data: types.UpdateVisitContextData
  ): Promise<types.VisitContextAndDisclaimersAcknowledged> {
    const instance = this.get();
    const visitContext = instance.visitContext as VisitContext;
    let disclaimersAcknowledged = instance.disclaimersAcknowledged as boolean;
    const form = data.updateVisitContextForm;

    const updatedVisitContext = await this.visitService.updateVisitContext(visitContext, form);

    if (typeof form.disclaimersAcknowledged === 'boolean') {
      ({ disclaimersAcknowledged } = form);
    }

    this.set({
      visitContext: updatedVisitContext,
      disclaimersAcknowledged
    });

    return {
      visitContext: updatedVisitContext,
      disclaimersAcknowledged
    };
  }

  // //////////////////////////////////////////////// //
  // //////////////////////////////////////////////// //
  // //////////////////////////////////////////////// //

  // ///// //
  // Visit //
  // ///// //

  /**
   * Method (promise): createVisit
   * @desc Creates visit based off the visit context.
   */
  async createVisit(): Promise<Visit> {
    const instance = this.get();
    const visitContext = instance.visitContext as VisitContext;

    const updatedVisit = await this.visitService.createOrUpdateVisit(visitContext);
    const visitWithCost = await this.visitService.waitForVisitCostCalculationToFinish(updatedVisit);

    this.set({ visit: visitWithCost });
    return visitWithCost;
  }

  /**
   * Method (promise): updateCouponCode
   * @desc Attempts to get coupon code and returns the visit with the new cost applied to it.
   * @param {Object} data
   * @param {string} data.couponCode
   */
  async updateCouponCode(data: types.UpdateCouponCodeData): Promise<Visit> {
    const instance = this.get();
    const visit = instance.visit as Visit;

    const visitCost = await this.visitService.applyCouponCode(visit, data.couponCode);
    visit.cost = visitCost;
    return visit;
  }

  /**
   * Method (promise): findActiveVisit
   * @desc Attempts to find the active visit the consumer was in.
   */
  async findActiveVisit(): Promise<Visit | null> {
    const instance = this.get();
    const activeConsumer = instance.activeConsumer as Consumer;

    if (!activeConsumer) {
      return null;
    }

    const activeVisit = await this.visitService.findActiveVisit(activeConsumer);

    if (activeVisit) {
      if (activeConsumer.id.persistentId !== activeVisit.consumer.id.persistentId) {
        this.set({
          activeConsumer: activeVisit.consumer
        });
      }
      this.set({
        visit: activeVisit
      });
    }

    return activeVisit;
  }

  /**
   * Method (promise): startVisit
   * @desc Starts visit based off created visit.
   * @param {Object} data
   * @param {Object} data.visit
   */
  async startVisit(data: types.VisitData): Promise<Visit> {
    const instance = this.get();

    let visit: Visit;

    if (instance.specialty) {
      visit = await this.visitService.findFirstAvailable(data.visit);
      visit = await this.visitService.startVisit(visit);
    } else {
      visit = await this.visitService.startVisit(data.visit);
    }

    this.set({ visit });
    return visit;
  }

  /**
   * Method: getTelehealthVideoInstallUrl
   * @desc Gets base url for download telehealth video
   */
  getTelehealthVideoInstallUrl(): string {
    return this.visitService.telehealthVideoInstallUrl;
  }

  /**
   * Method (promise): launchTelehealthVideo
   * @desc Launches the installed telehealth video application
   * @param {Object} data
   * @param {Object} data.visit
   */
  launchTelehealthVideo(data: types.VisitData): Promise<boolean> {
    return this.visitService.launchTelehealthVideo(data.visit);
  }

  /**
   * Method (promise): processVisit
   * @desc Processes the started visit and handles the visit event process.
   * @param {Object} data
   * @param {Object} data.visit
   */
  processVisit(data: types.VisitData): Promise<void> {
    if (useWebRTC(data.visit)) {
      return this.visitService.processWebRTCVisit(
        data.visit,
        (processVisit, processType, processStep) => {
          this.set({ visit: processVisit });
          amwellBus.emit('visitChange', {
            visit: processVisit,
            type: processType,
            step: processStep
          });
        }
      );
    }

    return this.visitService.processTelehealthVideoVisit(
      data.visit,
      (processVisit, processType, processStep) => {
        this.set({ visit: data.visit });
        amwellBus.emit('visitChange', {
          visit: processVisit,
          type: processType,
          step: processStep
        });

        if (processStep === types.VisitStep.STARTED) {
          this.visitService.startTelehealthVideoVisit(
            processVisit,
            (startVisit, startType, startStatus) => {
              amwellBus.emit('visitChange', {
                visit: startVisit,
                type: startType,
                step: startStatus
              });
            }
          );
        }
      }
    );
  }

  /**
   * Method (promise): startWebRTCVisit
   * @desc Starts the webRTC visit
   * @param {Object} data
   * @param {Object} data.vist
   * @param {Object} data.contextElements
   */
  startWebRTCVisit(data: types.StartWebRTCVisitData): Promise<VideoConsole> {
    return this.visitService.startWebRTCVisit(
      data.visit,
      data.contextElements,
      (visit, type, step) => {
        amwellBus.emit('visitChange', { visit, type, step });
      }
    );
  }

  /**
   * Method (promise): cancelVisit
   * @desc Cancel visit with provider
   * @param {Object} data
   * @param {Object} data.visit
   */
  async cancelVisit(data: types.VisitData): Promise<void> {
    await this.visitService.cancelVisit(data.visit);
    this.visitService.waitForVisitToFinish(data.visit);
  }

  /**
   * Method (promise): endVisit
   * @desc End visit manually
   * @param {Object} data
   * @param {Object} data.visit
   */
  async endVisit(data: types.VisitData): Promise<void> {
    await this.visitService.endVisit(data.visit);
    this.visitService.waitForVisitToFinish(data.visit);
  }

  /**
   * Method (promise): acceptSuggestedTransfer
   * @desc user has been suggested a transfer and they would like to accept
   * @param {Object} data
   * @param {Object} data.visit
   */
  acceptSuggestedTransfer(data: types.VisitData): Promise<void> {
    // Do not return visit from service.  The updated visit will come in on the waitingForProviderJoin events
    return this.visitService.acceptSuggestedTransfer(data.visit);
  }

  /**
   * Method (promise): handleTransfer
   * @desc user has confirmed transfer this will now complete the transfer
   * @param {Object} data
   * @param {Object} data.visit
   */
  async handleTransfer(data: types.VisitData): Promise<types.VisitTransfer> {
    const instance = this.get();
    const hydrated = this.hydrate({ provider: data.visit.providerForTransfer });

    const provider = hydrated.provider as ProviderDetails;
    const disclaimersAcknowledged = instance.disclaimersAcknowledged as boolean;
    const visitContext = instance.visitContext as VisitContext;

    const transfer = await this.visitService.handleTransfer(data.visit);

    if (visitContext?.callbackNumber) {
      transfer.visitContext.callbackNumber = visitContext.callbackNumber;
    }

    if (transfer.isQuickTransfer) {
      this.set({
        provider: data.visit.providerForTransfer,
        visitContext: transfer.visitContext,
        visit: transfer.visit
      });

      return {
        provider,
        disclaimersAcknowledged,
        isQuickTransfer: transfer.isQuickTransfer,
        visitContext: transfer.visitContext,
        visit: transfer.visit
      };
    }

    this.set({ provider: data.visit.providerForTransfer, visitContext: transfer.visitContext });
    await this.remove({
      keys: [types.StorageKey.VISIT, types.StorageKey.DISCLAIMERS_ACKNOWLEDGED]
    });

    return {
      provider,
      disclaimersAcknowledged: false,
      isQuickTransfer: transfer.isQuickTransfer,
      visitContext: transfer.visitContext,
      visit: null
    };
  }

  /**
   * Method (promise): getVisitSummary
   * @desc Gets visit summary
   */
  async getVisitSummary(): Promise<VisitSummary> {
    const instance = this.get();
    const visit = instance.visit as Visit;

    const visitSummary = await this.visitService.getVisitSummary(visit);

    this.set({ visitSummary });
    return visitSummary;
  }

  /**
   * Method (promise): declineTransfer
   * @desc declines transfer
   * @param {Object} data
   * @param {boolean} data.dontAskAgain
   */
  async declineSuggestedTransfer(data: types.DeclineSuggestedTransferData): Promise<void> {
    const instance = this.get();
    const visit = instance.visit as Visit;

    const updateVisit = await this.visitService.declineSuggestedTransfer(visit, data.dontAskAgain);
    this.set({ visit: updateVisit });
  }

  /**
   * Method (promise): setVisitSummary
   * @desc Sends the visit summary by fax and/or email address
   * @param {Object} data
   * @param {boolean} data.hipaaNoticeAccepted
   * @param {string[]} data.emailAddresses
   * @param {string[]} data.faxNumbers
   */
  async sendVisitSummary(data: types.SendVisitSummaryData): Promise<VisitSummary> {
    const instance = this.get();
    const visitSummary = instance.visitSummary as VisitSummary;

    const updatedVisitSummary = await this.visitService.sendVisitSummary(
      visitSummary,
      data.hipaaNoticeAccepted,
      data.emailAddresses,
      data.faxNumbers
    );

    this.set({ visitSummary: updatedVisitSummary });
    return updatedVisitSummary;
  }

  /**
   * Method (promise): submitVisitFeedback
   * @param {Object} data
   * @param {number} data.providerRating
   * @param {number} data.engagementRating
   * @param {string} data.feedbackQuestion
   * @param {string} data.feedbackAnswer
   */
  async submitVisitFeedback(data: types.SubmitVisitFeedbackData): Promise<boolean> {
    const instance = this.get();
    const visitSummary = instance.visitSummary as VisitSummary;

    await Promise.all([
      this.visitService.addRating(visitSummary, data.providerRating, data.engagementRating),
      this.visitService.addFeedback(visitSummary, data.feedbackQuestion, data.feedbackAnswer)
    ]);

    return true;
  }

  /**
   * Method (promise): setupWaitingRoomAlerts
   * @desc Sets up waiting room alerts for sms messaging
   * @param {Object} data
   * @param {number} data.phoneNumber
   * @param {boolean} data.providerReadyAlert
   * @param {boolean} data.movedUpInQueueAlert
   */
  setupWaitingRoomAlerts(data: types.SetupWaitingRoomAlertsData): Promise<boolean> {
    const instance = this.get();
    const visit = instance.visit as Visit;
    return this.visitService.setupWaitingRoomAlerts(
      visit,
      data.phoneNumber,
      data.providerReadyAlert,
      data.movedUpInQueueAlert
    );
  }

  /**
   * Method (promise): addChatMessage
   * @desc Allows consumer to send chat message once chat has been initiated
   * @param {Object} data
   * @param {string} data.message
   * @param {number} data.lastPolledChatOrdinal
   */
  addChatMessage(data: types.AddChatMessageData): Promise<ChatReport> {
    const instance = this.get();
    const visit = instance.visit as Visit;
    return this.visitService.addChatMessage(visit, data.message, data.lastPolledChatOrdinal);
  }

  /**
   * Method: (promise) getSpeedPass
   * @desc Gets a speed pass if available
   * @param {Object} data
   * @param {Object} data.provider
   */
  getSpeedPass(data: types.GetSpeedPassData) {
    const instance = this.get();
    const activeConsumer = instance.activeConsumer as Consumer;
    const { provider } = data;
    const firstAvailableConfiguration = instance.specialty as FirstAvailableConfiguration;
    const providerOrFirstAvailableConfiguration = provider || firstAvailableConfiguration;

    if (!activeConsumer) {
      return null;
    }

    return this.visitService.getSpeedPass(activeConsumer, providerOrFirstAvailableConfiguration);
  }

  async createVisitFromSpeedPass(data: types.CreateVisitFromSpeedPassData) {
    const { speedPass } = data;

    const visit = await this.visitService.createVisitFromSpeedPass(speedPass);
    const [visitWithCost, visitContext] = await Promise.all([
      this.visitService.waitForVisitCostCalculationToFinish(visit),
      this.getVisitContext()
    ]);
    const payload = { ...visitContext, visit: visitWithCost, disclaimersAcknowledged: true };
    this.set(payload);
    return payload;
  }
}

export default new AmwellClient();
